import { useContext, useState, useEffect, useRef, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import ListView from "@common/components/ListDetailViews/ListView";
import { useErrorNotification } from "@common/components/Notification";
import { WithGrow } from "@common/components/WithGrow";
import { Box, Checkbox, Divider, Tooltip } from "@mui/material";
import { TicketListContext, TicketProvider } from "@pages/Tickets/context";
import TicketDrawer from "@pages/Tickets/Drawer";
import { getTickets, getTicketsCounters } from "@services/tickets";
import { Ticket, TicketStatus, TicketsCounters } from "@services/tickets/types";
import { useTranslation } from "react-i18next";
import TicketCount from "./TicketCount";
import { getColumns } from "./colDef";
import {
  TicketContainerProps,
  TicketPriorityCounts,
  canAddTicket,
  defaultTicketCounts,
  getIdObject,
  priorities,
} from "./const";
import FilteringBar from "@common/components/FilteringBar";
import { FiltersTypeImpl, useFilters } from "@common/utils/filters";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import mappers from "@common/utils/filters/mappers";
import { ListPage } from "@services/common/types";
import { CheckCircleRounded } from "@mui/icons-material";
import ActionBarButton from "@common/components/Button/ActionBarButton";
import CloseTicketDrawer from "./CloseTicketDrawer";
import { CloseCaseTicketProvider } from "@pages/Tickets/Drawer/CloseCaseDialog/context";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import {
  TIME_FILTER_SEARCH_KEY,
  getTimesForTicketsFilter,
} from "@common/utils/filters/mappers/tickets";
import {
  TicketEntity,
  mapper,
  sortingMapper,
} from "@pages/Tickets/ListView/colDefs";
import { canBulkChange, canEditTicket } from "@common/utils/user";
import {
  FilterColumnContext,
  FilterColumnsProvider,
  GetColumnsFunctionType,
  TabValue,
} from "../FilterColumn/context";
import { useGlobalListPreferences } from "@common/components/GlobalPreferences/hooks";
import { SpecificPreferencesProvider } from "@common/components/SpecificPreferences";
import { ListViewPreferenceKey } from "@services/userPreferences/types";
import {
  attachPreferenceFunctions,
  sortColumnOrder,
} from "@common/components/SpecificPreferences/utils";
import { useSpecificListPreferences } from "@common/components/SpecificPreferences/hooks";

const TicketTab = (props: TicketContainerProps) => {
  const { type, id } = props;
  const [filters, setFilters] = useFilters(FiltersTypeImpl.Tickets);
  const [counts, setCounts] = useState(defaultTicketCounts);
  const [closeTicketsCount, setCloseTicketsCount] = useState<
    number | undefined
  >(undefined);
  const [searchParams] = useSearchParams();
  const {
    list,
    sort,
    dispatch,
    total,
    page = 1,
  } = useContext(TicketListContext);
  const [{ pageSize }] = useGlobalListPreferences();
  const dispatchError = useErrorNotification();
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const [selected, setSelected] = useState<TicketEntity["id"][]>([]);

  const [user] = useAuthenticatedUser();
  const { roomid, accountid, locationid } = useParams();
  const [openCloseTicket, setOpenCloseTicket] = useState<boolean>(false);
  const [
    { columnOrder, columnWidths },
    setSpecificListPreferences,
    { updateWidth },
  ] = useSpecificListPreferences();
  const location = useLocation();

  const canCloseTicket = canEditTicket(user);
  const canBulk = canBulkChange(user);
  //to delete when feature is implemented for meetings and locations
  const elementsToHide = ["locations", "meetings"];
  const canShowCloseTicketButton =
    !elementsToHide.some((search) =>
      location.pathname.split("/").includes(search)
    ) &&
    canCloseTicket &&
    canBulk;

  useEffect(() => {
    dispatch("reset");
  }, [searchParams, pageSize]);

  useEffect(() => {
    if (list === undefined) refresh();
  }, [list]);

  const setPage = (page: number) => {
    dispatch({ page: page, list: undefined });
  };

  const getFilters = () => {
    const timeFilterType = decodeURI(
      searchParams.get(TIME_FILTER_SEARCH_KEY) ?? ""
    );
    const filters = mappers.Tickets.fromSearchParams.toSL(searchParams);

    let {
      ["start.after"]: start,
      ["end.before"]: end,
      ...otherFilters
    } = filters;

    if (timeFilterType !== "" && timeFilterType !== "Custom time period") {
      [start, end] = getTimesForTicketsFilter(
        timeFilterType as TicketsTimeFilter
      );
    }
    const { status, ...accountFiltersWithoutStatus } = getIdObject(id, type);
    return { start, end, status, accountFiltersWithoutStatus, otherFilters };
  };

  const getList = async () => {
    try {
      const { start, end, accountFiltersWithoutStatus, otherFilters } =
        getFilters();

      let tickets: ListPage<Ticket>;
      tickets = await getTickets(
        (page - 1) * pageSize,
        undefined,
        pageSize,
        start,
        end,
        `${sortingMapper(sort.column)} ${sort.direction}`,
        {
          ...accountFiltersWithoutStatus,
          ...otherFilters,
        }
      );

      dispatch({ ...tickets, list: tickets.list.map(mapper) });
    } catch (e) {
      dispatchError(e);
    }
  };

  const TicketPriorityCount = async () => {
    setLoading(true);
    try {
      const { start, end, accountFiltersWithoutStatus, otherFilters } =
        getFilters();

      const response = await getTicketsCounters({
        ...accountFiltersWithoutStatus,
        ...otherFilters,
        ...{ startDate: start, endDate: end },
      });

      setCounts(convertCounters(response));

      if (otherFilters.status === undefined) {
        await closeAllTicketCounter();
      } else if (
        otherFilters.status.length === 1 &&
        otherFilters.status.includes("Closed")
      ) {
        setCloseTicketsCount(undefined);
      } else if (otherFilters.status.includes("Closed")) {
        await closeAllTicketCounter();
      } else {
        setCloseTicketsCount(response.ticketsTotal);
      }
    } catch (error) {
      dispatchError(error);
    } finally {
      setLoading(false);
    }
  };

  const closeAllTicketCounter = async () => {
    try {
      const { start, end, accountFiltersWithoutStatus, otherFilters } =
        getFilters();

      const filteredStatus = otherFilters.status
        ? otherFilters.status.filter((statut) => statut !== "Closed")
        : ["Open", "ClosePending"];

      const response = await getTicketsCounters({
        ...accountFiltersWithoutStatus,
        ...otherFilters,
        status: filteredStatus,
        ...{ startDate: start, endDate: end },
      });

      setCloseTicketsCount(response.ticketsTotal);
    } catch (error) {
      dispatchError(error);
    }
  };

  const convertCounters = (counter: TicketsCounters) => {
    return {
      Informational: counter.ticketsInformational,
      Critical: counter.ticketsCritical,
      Major: counter.ticketsMajor,
      Minor: counter.ticketsMinor,
    } as TicketPriorityCounts;
  };

  const onCloseTicketDrawer = () => {
    setOpenCloseTicket(false);
    refresh();
  };

  const { activeColumnsList } = useContext(FilterColumnContext);

  const RowCheckbox = (props: SelectorBehaviorProps) => {
    const { disabled } = props;
    return (
      <Tooltip
        title={
          disabled
            ? t("This ticket cannot be closed because it is already closed.")
            : ""
        }
        arrow
        placement="bottom-end"
      >
        <Box>
          <Checkbox {...props} disabled={disabled} />
        </Box>
      </Tooltip>
    );
  };

  const refresh = () => {
    TicketPriorityCount();
    getList();
  };

  const columns = useMemo(() => {
    const columns = getColumns(
      (id) => dispatch({ ticketId: `${id}` }),
      t
    ).filter((c) => activeColumnsList?.includes(c.key) ?? true);
    const defaultSortedColumns = activeColumnsList
      ? sortColumnOrder(columns, activeColumnsList)
      : columns;
    return attachPreferenceFunctions(defaultSortedColumns)
      .applyColumnSizes(columnWidths)
      .sortColumnOrder(columnOrder);
  }, [t, sort, activeColumnsList, columnOrder, columnWidths]);

  const canEdit = canEditTicket(user);

  return (
    <WithGrow
      direction="column"
      fixed={
        <Grid
          container
          wrap="nowrap"
          direction="row"
          sx={{
            gap: (theme) => theme.spacing(2),
            maxWidth: "950px",
            paddingLeft: "10%",
          }}
        >
          {priorities.map((value) => (
            <Grid
              key={value}
              sx={{
                flexBasis: "25%",
              }}
            >
              <TicketCount
                key={value}
                priority={value}
                count={counts[value]}
                loading={loading}
              />
            </Grid>
          ))}
        </Grid>
      }
      sx={{
        "& table": { "& th": { borderTop: "none" } },
        gap: "1rem",
      }}
    >
      <CloseCaseTicketProvider>
        <CloseTicketDrawer
          filters={filters}
          open={openCloseTicket}
          onClose={() => {
            onCloseTicketDrawer();
          }}
          tickets={selected.length > 0 ? selected.map(Number) : []}
          totalTickets={closeTicketsCount}
          parentId={id}
          parentEntityClass={type.toUpperCase()}
        />
      </CloseCaseTicketProvider>
      <WithGrow
        direction="column"
        fixed={
          <>
            <Divider orientation="horizontal" flexItem />
            <Grid
              sx={{
                display: "flex",
                width: "100%",
              }}
            >
              <FilteringBar
                disableAddButton={!canEdit}
                filters={filters}
                setFilters={(f) => setFilters([...f])}
                type={FiltersTypeImpl.Tickets}
                loading={list === undefined}
                refetch={() => refresh()}
                onAdd={
                  canAddTicket(user, roomid, accountid, locationid)
                    ? () => {
                        dispatch("openNewTicket");
                      }
                    : undefined
                }
                closeTicketButton={
                  canShowCloseTicketButton ? (
                    <ActionBarButton
                      startIcon={<CheckCircleRounded />}
                      ignoreLocalization
                      label={`${
                        selected.length === 0
                          ? t("Close all tickets")
                          : selected.length === 1
                          ? t("Close {{count}} ticket", {
                              count: selected.length,
                            })
                          : t("Close {{count}} tickets", {
                              count: selected.length,
                            })
                      }`}
                      onClick={() => setOpenCloseTicket(true)}
                      disabled={
                        (!closeTicketsCount || closeTicketsCount === 0) &&
                        selected.length === 0
                      }
                    />
                  ) : undefined
                }
              />
            </Grid>
            <Divider orientation="horizontal" flexItem />
          </>
        }
      >
        <ListView
          loading={list === undefined}
          data={list ?? []}
          columns={columns}
          pinFirstColumn
          onColumnsOrderChange={(order) =>
            setSpecificListPreferences({ columnOrder: order.map((c) => c.key) })
          }
          onColumnWidthChange={updateWidth}
          total={total}
          page={page}
          selectable={canShowCloseTicketButton}
          onSelect={(selected) => {
            setSelected(selected);
          }}
          isRowSelectable={(row) =>
            (row.state ?? TicketStatus.Closed) !== TicketStatus.Closed
          }
          customSelector={RowCheckbox}
          identityKey="id"
          sort={sort}
          onSort={(column, direction) =>
            dispatch({ sort: { column, direction } })
          }
          pageSize={pageSize}
          setPage={setPage}
          testId="tickets-list-view"
        />
        <TicketDrawer onClose={(saved) => saved && refresh()} />
      </WithGrow>
    </WithGrow>
  );
};

const Container = (props: TicketContainerProps) => {
  const [filters, setFilters] = useFilters(FiltersTypeImpl.Tickets);
  const isLoadedRef = useRef<boolean>(filters.length > 0);
  useEffect(() => {
    if (isLoadedRef.current === false) {
      isLoadedRef.current = true;
      let defaultFilters = [
        ...filters,
        ...mappers.Tickets.fromSL.toDefinition({
          status: ["Open", "ClosePending"],
        }),
      ];
      setFilters(defaultFilters);
    }
  }, []);

  const { t } = useTranslation();

  const getColumnsFunction: GetColumnsFunctionType<TicketEntity> = () => {
    return getColumns(() => {}, t);
  };

  return !isLoadedRef.current ? (
    <></>
  ) : (
    <SpecificPreferencesProvider assetKey={ListViewPreferenceKey.TICKET_TAB}>
      <FilterColumnsProvider
        getColumnFunction={getColumnsFunction}
        tabName="Tickets"
        tabValue={TabValue.Info}
      >
        <TicketProvider>
          <TicketTab {...props} />
        </TicketProvider>
      </FilterColumnsProvider>
    </SpecificPreferencesProvider>
  );
};

export default Container;
